/**
 * Tiny wrapper around SheetJS that turns a (rows, columns) pair into a
 * downloaded .xlsx file. Designed to be called from per-table Export buttons.
 *
 *   columns = [{ key: "firm_name", label: "Firm", format?: v => string }, ...]
 *   rows    = [{ firm_name: "...", ... }, ...]
 *
 * If `format` is omitted, the raw value is written (numbers stay numeric,
 * which keeps Excel auto-sums working). Missing fields are written as "".
 */
import * as XLSX from "xlsx";

const todayStamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export function exportToXlsx({ filename, sheetName = "Sheet1", columns, rows }) {
  if (!rows || rows.length === 0) {
    // SheetJS happily writes an empty sheet, but warn the caller via console.
    // eslint-disable-next-line no-console
    console.warn(`[exportToXlsx] No rows to export for ${filename}`);
  }

  const header = columns.map((c) => c.label);
  const aoa = [
    header,
    ...(rows || []).map((row) =>
      columns.map((c) => {
        const raw = row[c.key];
        if (raw === undefined || raw === null) return "";
        if (typeof c.format === "function") return c.format(raw, row);
        return raw;
      })
    ),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Auto-width based on the longest cell in each column (capped at 60 chars).
  ws["!cols"] = columns.map((c, idx) => {
    const max = aoa.reduce((m, r) => {
      const v = r[idx];
      const s = v == null ? "" : String(v);
      return Math.max(m, s.length);
    }, c.label.length);
    return { wch: Math.min(Math.max(max + 2, 10), 60) };
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));

  const finalName = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
  XLSX.writeFile(wb, finalName);
}

export const exportStamp = todayStamp;
